// =====================================================================
// /admin/projects - Gestionare proiecte (Faza 2)
// =====================================================================
// Functionalitati:
// - Listare cu filtre (kind, active) + search live
// - Creare proiect nou (modal: name + kind + approbateur + backup)
// - Editare proiect (modal cu Optimistic Locking)
// - Toggle active/archived (confirm popup)
// - Conflict 409 -> modal cu 3 optiuni
// - Approbateur si backup TREBUIE ADMIN/COORDINATOR si DIFERITI
// =====================================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projects as projectsApi, users as usersApi, tasks as tasksApi, errMsg } from '../../services/api.js';
import { enregistrerReponse, messageDeBlob } from '../../lib/telechargement.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { marqueurInactif } from '../../lib/tri-personnes.js';
import { libelleRole } from '../../lib/roles.js';

// Miroir exact de validateApproverRole (backend/src/services/project.service.js:60).
const ROLES_APPROBATEUR = ['SUPER_ADMIN', 'ADMIN', 'COORDINATOR'];
// La règle d'éligibilité, écrite UNE fois : compte actif ET rôle approbateur.
const estEligible = (u) => u.isActive !== false && ROLES_APPROBATEUR.includes(u.role);
const KIND_VALUES = ['CLIENT', 'INTERNE'];
const KIND_LABELS = {
  CLIENT: 'Externe',
  INTERNE: 'Interne',
};

const AVATAR_GRADIENTS = [
  ['#6366f1', '#8b5cf6'],
  ['#ec4899', '#f43f5e'],
  ['#06b6d4', '#3b82f6'],
  ['#10b981', '#14b8a6'],
  ['#f59e0b', '#ef4444'],
  ['#8b5cf6', '#d946ef'],
  ['#0ea5e9', '#22d3ee'],
];

function getInitials(name) {
  if (!name) return '??';
  const parts = name.trim().replace(/[^\w\s]/g, '').split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getGradient(id) {
  let hash = 0;
  for (let i = 0; i < (id || '').length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

export default function AdminProjectsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [eligibleApprovers, setEligibleApprovers] = useState([]);
  // Si la liste ne vient pas, le dire : deux champs obligatoires vides sans un
  // mot, c'est un écran qui refuse tout sans expliquer pourquoi.
  const [approversErr, setApproversErr] = useState(null);
  // Tous les comptes, pour retrouver l'approbateur ACTUEL d'un projet même
  // s'il n'est plus éligible (rôle changé) : sinon le champ paraît vide.
  const [tousLesComptes, setTousLesComptes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [filterKind, setFilterKind] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  const [editingProject, setEditingProject] = useState(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  const canCreate = can('projects.create.all');
  const canUpdate = can('projects.update.all');
  const canArchive = can('projects.delete.all');
  const canExportTaches = can('tasks.read.all');
  const [exportTaches, setExportTaches] = useState(false);

  // 22-10-2026 (demande Luca) : toutes les tâches de tous les projets, en Excel, pour les réorganiser.
  const exporterTaches = async () => {
    setExportTaches(true);
    try {
      enregistrerReponse(await tasksApi.exportAll('xlsx'), 'zeus-taches.xlsx');
    } catch (e) {
      alert(await messageDeBlob(e));
    } finally {
      setExportTaches(false);
    }
  };

  const loadProjects = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterKind) params.kind = filterKind;
      if (filterActive !== 'all') params.isActive = filterActive;
      if (search.trim()) params.search = search.trim();

      const data = await projectsApi.list(params);
      setProjects(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [filterKind, filterActive]);

  // Search debounce 300ms
  useEffect(() => {
    const t = setTimeout(loadProjects, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Qui peut approuver : la liste est celle du SERVEUR, pas une autre.
  // backend/src/services/project.service.js:60 exige SUPER_ADMIN, ADMIN ou
  // COORDINATOR. L'écran proposait OPERATOR_CLIENT — toujours refusé à
  // l'enregistrement — et cachait COORDINATOR, le rôle que l'aide sous le champ
  // citait elle-même. Deux listes pour une seule règle : une de trop.
  //
  // Comptes INACTIFS compris, mais NON sélectionnables : quand l'approbateur
  // d'un projet partait, le champ paraissait VIDE alors qu'il était plein, et
  // l'enregistrement échouait sur un message qui ne désignait pas le champ.
  useEffect(() => {
    usersApi.list({ champs: 'minimal' })   // identité seulement : pas de dossier pour une liste déroulante
      .then(allUsers => {
        setTousLesComptes(allUsers);
        const eligible = allUsers.filter(estEligible);   // les inactifs ne polluent pas les listes de création
        setEligibleApprovers(eligible);
      })
      .catch((e) => { setEligibleApprovers([]); setApproversErr(errMsg(e)); });
  }, []);

  const handleToggleActive = async (project) => {
    const action = project.isActive ? 'archiver' : 'reactiver';
    if (!window.confirm(`Voulez-vous ${action} le projet "${project.name}" ?`)) return;

    try {
      await projectsApi.toggleActive(project.id, project.updatedAt);
      await loadProjects();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data.details?.currentData) {
        setConflictData({
          type: 'toggle',
          project,
          serverData: err.response.data.details.currentData,
        });
      } else {
        alert(err.response?.data?.message || 'Erreur');
      }
    }
  };

  return (
    <div className="admin-users">
      <div className="page-header">
        <h1>
          Projets <span className="count-badge">{projects.length}</span>
        </h1>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => setCreatingProject(true)}>
            + Nouveau projet
          </button>
        )}
        {canCreate && (
          <button className="btn btn-secondary" onClick={() => setCreatingFromTemplate(true)} style={{marginLeft:'0.5rem'}}>
            + Depuis modele
          </button>
        )}
        {canExportTaches && (
          <button className="btn btn-secondary" onClick={exporterTaches} disabled={exportTaches} style={{marginLeft:'0.5rem'}}
            title="Toutes les tâches de tous les projets (actives et inactives), avec leur usage dans les feuilles de temps">
            {exportTaches ? '⏳ Export…' : '📥 Exporter toutes les tâches (Excel)'}
          </button>
        )}
      </div>

      <div className="toolbar">
        <input
          type="text"
          placeholder="Rechercher par nom..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />
        <select value={filterKind} onChange={e => setFilterKind(e.target.value)}>
          <option value="">Tous les types</option>
          {KIND_VALUES.map(k => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
        </select>
        <select value={filterActive} onChange={e => setFilterActive(e.target.value)}>
          <option value="all">Tous les statuts</option>
          <option value="true">Actifs uniquement</option>
          <option value="false">Archives uniquement</option>
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-wrapper">
        {loading ? (
          <div className="loading">Chargement...</div>
        ) : projects.length === 0 ? (
          <div className="empty">Aucun projet trouve</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Projet</th>
                <th>Type</th>
                <th>Approbateur</th>
                <th>Backup</th>
                <th>Taches</th>
                <th>Statut</th>
                {(canUpdate || canArchive) && <th></th>}
              </tr>
            </thead>
            <tbody>
              {projects.map(p => {
                const [c1, c2] = getGradient(p.id);
                return (
                  <tr key={p.id} className={p.isActive ? '' : 'row-inactive'}>
                    <td>
                      <div className="name-cell">
                        <span
                          className="avatar"
                          style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}
                        >
                          {getInitials(p.name)}
                        </span>
                        <div>
                          <div className="name">{p.name}</div>
                          <div className="username">{p.id}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${p.kind.toLowerCase()}`}>
                        {KIND_LABELS[p.kind] || p.kind}
                      </span>
                    </td>
                    <td>
                      <span className="approver-name">{p.primaryApprover?.name || '-'}</span>
                    </td>
                    <td>
                      <span className="approver-name">{p.backupApprover?.name || '-'}</span>
                    </td>
                    <td>
                      <span className="tasks-count">{p._count?.tasks ?? 0}</span>
                    </td>
                    <td>
                      <span className={`status status-${p.isActive ? 'active' : 'inactive'}`}>
                        <span className="status-dot"></span>
                        {p.isActive ? 'Actif' : 'Archive'}
                      </span>
                    </td>
                    {(canUpdate || canArchive) && (
                      <td className="actions">
                        <button className="btn-icon" onClick={() => navigate(`/admin/projects/${p.id}/tasks`)} title="Voir les taches">Taches</button>
                        {canUpdate && (
                          <button className="btn-icon" onClick={() => setEditingProject(p)} title="Modifier">
                            Edit
                          </button>
                        )}
                        {canArchive && (
                          <button
                            className="btn-icon"
                            onClick={() => handleToggleActive(p)}
                            title={p.isActive ? 'Archiver' : 'Reactiver'}
                          >
                            {p.isActive ? 'X' : 'OK'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {creatingProject && (
        <ProjectModal
          mode="create"
          eligibleApprovers={eligibleApprovers}
          tousLesComptes={tousLesComptes}
          approversErr={approversErr}
          onClose={() => setCreatingProject(false)}
          onSaved={(newId) => { setCreatingProject(false); if (newId) navigate(`/admin/projects/${newId}/tasks`); else loadProjects(); }}
        />
      )}

      {creatingFromTemplate && (
        <ProjectFromTemplateModal
          eligibleApprovers={eligibleApprovers}
          approversErr={approversErr}
          onClose={() => setCreatingFromTemplate(false)}
          onSaved={(newId) => { setCreatingFromTemplate(false); if (newId) navigate(`/admin/projects/${newId}/tasks`); else loadProjects(); }}
        />
      )}

      {editingProject && (
        <ProjectModal
          mode="edit"
          project={editingProject}
          eligibleApprovers={eligibleApprovers}
          tousLesComptes={tousLesComptes}
          approversErr={approversErr}
          onClose={() => setEditingProject(null)}
          onSaved={() => { setEditingProject(null); loadProjects(); }}
          onConflict={(data) => { setEditingProject(null); setConflictData(data); }}
        />
      )}

      {conflictData && (
        <ConflictModal
          data={conflictData}
          onClose={() => setConflictData(null)}
          onResolved={() => { setConflictData(null); loadProjects(); }}
        />
      )}
    </div>
  );
}

// =====================================================================
// Modal Create/Edit Project
// =====================================================================
function ProjectModal({ mode, project, eligibleApprovers, tousLesComptes, approversErr, onClose, onSaved, onConflict }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({
    name: project?.name || '',
    kind: project?.kind || 'CLIENT',
    approbateurId: project?.approbateurId || '',
    backupApprobateurId: project?.backupApprobateurId || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const sameApprovers = form.approbateurId && form.backupApprobateurId &&
                        form.approbateurId === form.backupApprobateurId;
  // Le serveur refuse un approbateur désactivé à CHAQUE enregistrement
  // (project.service.js, USER_INACTIVE), même pour corriger une faute dans le nom.
  // Plutôt qu'un 400 après coup, on le dit sous le champ, avant.
  // Un approbateur peut ne plus convenir pour deux raisons : compte désactivé,
  // ou rôle changé (il n'est plus dans ROLES_APPROBATEUR). Dans les deux cas on
  // le MONTRE, marqué et non sélectionnable, et on dit sous le champ quoi faire.
  const actuelsIneligibles = (tousLesComptes || []).filter((u) =>
    (u.id === form.approbateurId || u.id === form.backupApprobateurId)
    && !eligibleApprovers.some((e) => e.id === u.id));
  const options = [...eligibleApprovers, ...actuelsIneligibles];
  const nestPlusEligible = (id) => options.some((u) => u.id === id && !estEligible(u));
  const principalParti = nestPlusEligible(form.approbateurId);
  const backupParti = nestPlusEligible(form.backupApprobateurId);
  const suffixe = (u) => marqueurInactif(u) || (estEligible(u) ? '' : ' (plus approbateur)');
  // Un approbateur parti n'empêche plus d'enregistrer le reste du projet : il est
  // signalé sous son champ, et on ne peut pas en CHOISIR un inéligible (options
  // désactivées).
  const canSubmit = form.name.trim() && form.kind && form.approbateurId && form.backupApprobateurId && !sameApprovers;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sameApprovers) {
      setErr('Approbateur et backup doivent etre differents');
      return;
    }
    setSaving(true);
    setErr(null);

    // En modification, les approbateurs ne partent que s'ils CHANGENT : le
    // serveur revalide tout approbateur reçu, et refusait donc de corriger le
    // nom d'un projet dont l'approbateur est parti. Hors du `try` : le conflit
    // (409) renvoie CE payload, pas le formulaire entier.
    const payload = { name: form.name.trim(), kind: form.kind };
    if (!isEdit || form.approbateurId !== project.approbateurId) payload.approbateurId = form.approbateurId;
    if (!isEdit || form.backupApprobateurId !== project.backupApprobateurId) payload.backupApprobateurId = form.backupApprobateurId;

    try {

      if (isEdit) {
        payload.expectedUpdatedAt = project.updatedAt;
        await projectsApi.update(project.id, payload);
        onSaved();
      } else {
        const created = await projectsApi.create(payload);
        onSaved(created?.id);
      }
    } catch (error) {
      // Optimistic locking conflict (has currentData in details)
      if (error.response?.status === 409 && error.response.data.details?.currentData) {
        if (onConflict) {
          onConflict({
            type: 'edit',
            project,
            myChanges: payload,
            serverData: error.response.data.details.currentData,
          });
          return;
        }
      }
      setErr(error.response?.data?.message || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Modifier projet' : 'Nouveau projet'}</h2>
          <button className="btn-close" onClick={onClose} type="button">x</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {err && <div className="alert alert-error">{err}</div>}

            <div className="form-row">
              <label>Nom *</label>
              <input
                type="text"
                required
                maxLength={200}
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: ALLERGO ou Brain E-log - Sales"
              />
              <small>Convention: MAJUSCULES pour clients, prefix "Brain E-log -" pour internes</small>
            </div>

            <div className="form-row">
              <label>Type *</label>
              <select
                value={form.kind}
                onChange={e => setForm({ ...form, kind: e.target.value })}
              >
                {KIND_VALUES.map(k => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
              </select>
            </div>

            <div className="form-row">
              <label>Approbateur principal *</label>
              <select
                required
                value={form.approbateurId}
                onChange={e => setForm({ ...form, approbateurId: e.target.value })}
              >
                <option value="">-- Selectionner --</option>
                {options.map(u => (
                  <option key={u.id} value={u.id} disabled={!estEligible(u)}>
                    {u.name} ({libelleRole(u.role)}){suffixe(u)}
                  </option>
                ))}
              </select>
              <small>Doit être {ROLES_APPROBATEUR.map(libelleRole).join(', ')}</small>
              {approversErr && <small style={{ color: '#b91c1c', display: 'block' }}>La liste des approbateurs n'a pas pu être chargée : {approversErr}</small>}
              {principalParti && <small style={{ color: '#b91c1c', display: 'block' }}>Cette personne ne peut plus approuver : ses demandes d'approbation restent sans réponse. Choisissez un remplaçant.</small>}
            </div>

            <div className="form-row">
              <label>Backup approbateur *</label>
              <select
                required
                value={form.backupApprobateurId}
                onChange={e => setForm({ ...form, backupApprobateurId: e.target.value })}
              >
                <option value="">-- Selectionner --</option>
                {options.map(u => (
                  <option key={u.id} value={u.id} disabled={u.id === form.approbateurId || !estEligible(u)}>
                    {u.name} ({libelleRole(u.role)}){suffixe(u)}
                  </option>
                ))}
              </select>
              {sameApprovers ? (
                <small style={{ color: '#b91c1c' }}>
                  Doit etre different de l approbateur principal
                </small>
              ) : (
                <small>Active si le principal n'est pas disponible</small>
              )}
              {backupParti && <small style={{ color: '#b91c1c', display: 'block' }}>Cette personne ne peut plus approuver : ses demandes d'approbation restent sans réponse. Choisissez un remplaçant.</small>}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !canSubmit}>
              {saving ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Creer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =====================================================================
// Modal Conflict 409 - 3 optiuni
// =====================================================================
function ConflictModal({ data, onClose, onResolved }) {
  const handleOverwrite = async () => {
    try {
      const payload = {
        ...data.myChanges,
        expectedUpdatedAt: data.serverData.updatedAt,
      };
      await projectsApi.update(data.project.id, payload);
      onResolved();
    } catch (e) {
      alert('Erreur sauvegarde: ' + (e.response?.data?.message || e.message));
    }
  };

  const formatDate = (d) => {
    if (!d) return '?';
    return new Date(d).toLocaleString('fr-BE');
  };

  return (
    <div className="modal-backdrop">
      <div className="modal modal-conflict">
        <div className="modal-header">
          <h2>Conflit de modification</h2>
        </div>
        <div className="modal-body">
          <p>
            Le projet <strong>{data.project?.name}</strong> a ete modifie par un autre administrateur
            depuis que vous avez charge la page.
          </p>
          <div className="conflict-info">
            <div>Votre version: <strong>{formatDate(data.project?.updatedAt)}</strong></div>
            <div>Version serveur: <strong>{formatDate(data.serverData?.updatedAt)}</strong></div>
          </div>
          <p>Que souhaitez-vous faire ?</p>
        </div>
        <div className="modal-footer modal-footer-conflict">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button className="btn btn-secondary" onClick={onResolved}>
            Recharger
          </button>
          <button className="btn btn-danger" onClick={handleOverwrite}>
            Ecraser
          </button>
        </div>
      </div>
    </div>
  );
}
// =====================================================================
// Modal Create From Template (Faza 2.5)
// =====================================================================
function ProjectFromTemplateModal({ eligibleApprovers, approversErr, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', kind: 'CLIENT', approbateurId: '', backupApprobateurId: '' });
  const [templates, setTemplates] = useState([]);
  const [selectedTaskNames, setSelectedTaskNames] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    tasksApi.listTemplates()
      .then(t => { setTemplates(t); setLoadingTemplates(false); })
      .catch(() => { setErr('Impossible de charger les modeles'); setLoadingTemplates(false); });
  }, []);

  const filtered = templates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));

  const toggleTask = (name) => {
    const s = new Set(selectedTaskNames);
    if (s.has(name)) s.delete(name); else s.add(name);
    setSelectedTaskNames(s);
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every(t => selectedTaskNames.has(t.name));
  const toggleAll = () => {
    const s = new Set(selectedTaskNames);
    if (allFilteredSelected) filtered.forEach(t => s.delete(t.name));
    else filtered.forEach(t => s.add(t.name));
    setSelectedTaskNames(s);
  };

  const sameApprovers = form.approbateurId && form.backupApprobateurId && form.approbateurId === form.backupApprobateurId;
  const canSubmit = form.name.trim() && form.approbateurId && form.backupApprobateurId && !sameApprovers;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sameApprovers) { setErr('Approbateur et backup doivent etre differents'); return; }
    setSaving(true); setErr(null);
    try {
      const payload = {
        name: form.name.trim(),
        kind: form.kind,
        approbateurId: form.approbateurId,
        backupApprobateurId: form.backupApprobateurId,
        taskNames: Array.from(selectedTaskNames),
      };
      const created = await projectsApi.createFromTemplate(payload);
      onSaved(created?.id);
    } catch (error) {
      setErr(error.response?.data?.message || 'Erreur de sauvegarde');
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Nouveau projet — Depuis modele</h2>
          <button className="btn-close" onClick={onClose} type="button">x</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body modal-body-scroll">
            {err && <div className="alert alert-error">{err}</div>}

            <div className="form-row">
              <label>Nom *</label>
              <input type="text" required maxLength={200} value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Nouveau client XYZ" />
            </div>

            <div className="form-row">
              <label>Type</label>
              <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}>
                {KIND_VALUES.map(k => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
              </select>
            </div>

            {approversErr && <div className="alert alert-error">La liste des approbateurs n'a pas pu être chargée : {approversErr}</div>}
            <div className="form-row">
              <label>Approbateur principal *</label>
              <select required value={form.approbateurId}
                onChange={e => setForm({ ...form, approbateurId: e.target.value })}>
                <option value="">-- Selectionner --</option>
                {eligibleApprovers.map(u => <option key={u.id} value={u.id}>{u.name} ({libelleRole(u.role)})</option>)}
              </select>
            </div>

            <div className="form-row">
              <label>Backup approbateur *</label>
              <select required value={form.backupApprobateurId}
                onChange={e => setForm({ ...form, backupApprobateurId: e.target.value })}>
                <option value="">-- Selectionner --</option>
                {eligibleApprovers.map(u => (
                  <option key={u.id} value={u.id} disabled={u.id === form.approbateurId}>{u.name} ({libelleRole(u.role)})</option>
                ))}
              </select>
              {sameApprovers && <small style={{ color: '#b91c1c' }}>Doit etre different du principal</small>}
            </div>

            <div className="template-tasks-section">
              <div className="template-tasks-header">
                <h3>Taches a inclure ({selectedTaskNames.size}/{templates.length})</h3>
                <input type="text" placeholder="Rechercher..." value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="search-input search-input-sm" />
                <button type="button" className="btn btn-sm" onClick={toggleAll}>
                  {allFilteredSelected ? 'Tout decocher' : 'Tout cocher'}
                </button>
              </div>
              <div className="template-tasks-list">
                {loadingTemplates ? <div className="loading">Chargement...</div>
                  : filtered.length === 0 ? <div className="empty">Aucune tache</div>
                  : filtered.map(t => (
                    <label key={t.name} className="template-task-item">
                      <input type="checkbox" checked={selectedTaskNames.has(t.name)} onChange={() => toggleTask(t.name)} />
                      <span className="template-task-name">{t.name}</span>
                      <span className="template-task-count">{t.projectCount} proj.</span>
                    </label>
                  ))}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !canSubmit}>
              {saving ? 'Creation...' : `Creer avec ${selectedTaskNames.size} tache(s)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}